import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import type { Company, CreateCompanyInput, Contact, CreateContactInput } from '@/types';
import { createLogger } from '@/utils/debug';
import { companiesCollection, companyContactsCollection } from '@/config/collections';

const log = createLogger('Companies');

// ===========================================
// Companies Collection Reference
// ===========================================

const companiesRef = companiesCollection(db);

// ===========================================
// Helper: Convert Firestore document to Company
// ===========================================

function docToCompany(doc: any): Company {
  const data = doc.data();
  return {
    id: doc.id,
    name: data.name,
    type: data.type,
    address: data.address,
    city: data.city,
    state: data.state,
    zip: data.zip,
    phone: data.phone,
    email: data.email,
    website: data.website,
    notes: data.notes,
    // Sales rep assignment fields
    salesRepUserId: data.salesRepUserId,
    salesRepName: data.salesRepName,
    salesRepMondayPersonId: data.salesRepMondayPersonId,
    // Referral tracking
    referredMondayItemIds: data.referredMondayItemIds,
    referredProjectCodes: data.referredProjectCodes,
    // Partner commission configuration
    commissionType: data.commissionType,
    commissionValue: data.commissionValue,
    commissionCap: data.commissionCap,
    commissionTiers: data.commissionTiers,
    noteNotifyUserIds: data.noteNotifyUserIds,
    businessVisibility: data.businessVisibility,
    // Visit tracking
    lastVisitDate: data.lastVisitDate?.toDate(),
    lastVisitBy: data.lastVisitBy,
    lastVisitByName: data.lastVisitByName,
    lastVisitNotes: data.lastVisitNotes,
    nextFollowUpDate: data.nextFollowUpDate?.toDate(),
    skipVisitTracking: data.skipVisitTracking,
    // Temperature & outreach status
    temperature: data.temperature,
    outreachStatus: data.outreachStatus,
    primaryContactName: data.primaryContactName,
    // Inactive status
    isInactive: data.isInactive,
    // Communication settings
    communicationType: data.communicationType,
    reminderFrequency: data.reminderFrequency,
    nextReminderDate: data.nextReminderDate?.toDate(),
    customReminderDate: data.customReminderDate?.toDate(),
    createdBy: data.createdBy,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  };
}

function docToContact(doc: any, companyId: string): Contact {
  const data = doc.data();
  return {
    id: doc.id,
    companyId,
    firstName: data.firstName,
    lastName: data.lastName,
    title: data.title,
    email: data.email,
    phone: data.phone,
    mobile: data.mobile,
    isPrimary: data.isPrimary || false,
    notes: data.notes,
    salesRepUserId: data.salesRepUserId,
    salesRepName: data.salesRepName,
    commissionType: data.commissionType,
    commissionValue: data.commissionValue,
    commissionCap: data.commissionCap,
    commissionTiers: data.commissionTiers,
    createdBy: data.createdBy,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  };
}

// ===========================================
// Company CRUD Operations
// ===========================================

/**
 * Get all companies (sorted by name)
 */
export async function getCompanies(): Promise<Company[]> {
  return log.operation('getCompanies', async () => {
    const q = query(companiesRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    const companies = snapshot.docs.map(docToCompany);
    log.debug('getCompanies completed', { count: companies.length });
    return companies;
  });
}

/**
 * Get a single company by ID
 */
export async function getCompany(id: string): Promise<Company | null> {
  return log.operation('getCompany', async () => {
    const docRef = doc(companiesRef, id);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      log.debug('getCompany not found', { id });
      return null;
    }

    log.debug('getCompany found', { id });
    return docToCompany(snapshot);
  }, { id });
}

/**
 * Search companies by name (case-insensitive prefix match)
 */
export async function searchCompanies(searchTerm: string): Promise<Company[]> {
  // Firestore doesn't support case-insensitive search natively
  // We'll fetch all and filter client-side for now
  // For production with large datasets, consider Algolia or a search index
  const companies = await getCompanies();
  const lowerSearch = searchTerm.toLowerCase();

  return companies.filter((company) =>
    company.name.toLowerCase().includes(lowerSearch)
  );
}

/**
 * Create a new company
 */
export async function createCompany(
  input: CreateCompanyInput,
  userId: string
): Promise<Company> {
  return log.operation('createCompany', async () => {
    // Filter out undefined values - Firestore doesn't accept them
    const cleanedInput: Record<string, any> = {};
    for (const [key, value] of Object.entries(input)) {
      if (value !== undefined) {
        cleanedInput[key] = value;
      }
    }

    const dataToSave = {
      ...cleanedInput,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    console.log('[companies.createCompany] Input received:', input);
    console.log('[companies.createCompany] Data being saved to Firestore:', dataToSave);
    console.log('[companies.createCompany] salesRepUserId:', input.salesRepUserId);
    console.log('[companies.createCompany] salesRepName:', input.salesRepName);
    console.log('[companies.createCompany] salesRepMondayPersonId:', input.salesRepMondayPersonId);

    const docRef = await addDoc(companiesRef, dataToSave);

    // Fetch the created document to get server timestamp
    const created = await getDoc(docRef);
    const company = docToCompany(created);
    console.log('[companies.createCompany] Created company from Firestore:', company);
    log.info('createCompany success', { id: company.id, name: company.name });
    return company;
  }, { name: input.name, userId });
}

/**
 * Update an existing company
 */
export async function updateCompany(
  id: string,
  input: Partial<CreateCompanyInput>
): Promise<void> {
  return log.operation('updateCompany', async () => {
    const docRef = doc(companiesRef, id);

    // Filter out undefined values - Firestore doesn't accept them
    // If a field should be removed, it needs to use deleteField() explicitly
    const cleanedInput: Record<string, any> = {};
    for (const [key, value] of Object.entries(input)) {
      if (value !== undefined) {
        cleanedInput[key] = value;
      }
    }

    await updateDoc(docRef, {
      ...cleanedInput,
      updatedAt: serverTimestamp(),
    });
    log.info('updateCompany success', { id });
  }, { id, fields: Object.keys(input) });
}

/**
 * Delete a company
 */
export async function deleteCompany(id: string): Promise<void> {
  return log.operation('deleteCompany', async () => {
    const docRef = doc(companiesRef, id);
    await deleteDoc(docRef);
    log.info('deleteCompany success', { id });
  }, { id });
}

// ===========================================
// Contact CRUD Operations
// ===========================================

/**
 * Get contacts subcollection reference
 */
function getContactsRef(companyId: string) {
  return companyContactsCollection(db, companyId);
}

/**
 * Get ALL contacts across every company (collection group query).
 * Used for cross-company search on the Referral Sources page so typing a
 * contact name surfaces their company. Requires a Firestore collection-group
 * read rule on `contacts` for active CRM users.
 *
 * Errors are surfaced loudly via console.error so a permission / index issue
 * never silently turns into "search returns 0 contacts".
 */
export async function getAllContacts(): Promise<Contact[]> {
  try {
    return await log.operation('getAllContacts', async () => {
      const snapshot = await getDocs(collectionGroup(db, 'contacts'));
      const contacts: Contact[] = [];
      for (const docSnap of snapshot.docs) {
        const companyId = docSnap.ref.parent.parent?.id;
        if (!companyId) continue;
        contacts.push(docToContact(docSnap, companyId));
      }
      log.info('getAllContacts loaded', { count: contacts.length });
      return contacts;
    });
  } catch (err) {
    console.error('[getAllContacts] collection-group query failed:', err);
    throw err;
  }
}

/**
 * Get all contacts for a company
 */
export async function getContacts(companyId: string): Promise<Contact[]> {
  return log.operation('getContacts', async () => {
    const contactsRef = getContactsRef(companyId);
    const q = query(contactsRef, orderBy('lastName', 'asc'));
    const snapshot = await getDocs(q);
    const contacts = snapshot.docs.map((doc) => docToContact(doc, companyId));
    log.debug('getContacts completed', { companyId, count: contacts.length });
    return contacts;
  }, { companyId });
}

/**
 * Get a single contact
 */
export async function getContact(
  companyId: string,
  contactId: string
): Promise<Contact | null> {
  const docRef = doc(getContactsRef(companyId), contactId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return docToContact(snapshot, companyId);
}

/**
 * Create a new contact
 */
export async function createContact(
  companyId: string,
  input: CreateContactInput,
  userId: string
): Promise<Contact> {
  return log.operation('createContact', async () => {
    const contactsRef = getContactsRef(companyId);

    // If this is marked as primary, unset any existing primary contacts
    if (input.isPrimary) {
      log.debug('createContact: unsetting existing primary contacts', { companyId });
      const existingContacts = await getContacts(companyId);
      for (const contact of existingContacts) {
        if (contact.isPrimary) {
          await updateDoc(doc(contactsRef, contact.id), { isPrimary: false });
        }
      }
    }

    const docRef = await addDoc(contactsRef, {
      ...input,
      isPrimary: input.isPrimary || false,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const created = await getDoc(docRef);
    const contact = docToContact(created, companyId);
    log.info('createContact success', { companyId, contactId: contact.id, name: `${contact.firstName} ${contact.lastName}` });
    return contact;
  }, { companyId, userId });
}

/**
 * Update a contact
 */
export async function updateContact(
  companyId: string,
  contactId: string,
  input: Partial<CreateContactInput>
): Promise<void> {
  const contactsRef = getContactsRef(companyId);
  const docRef = doc(contactsRef, contactId);

  // If setting as primary, unset other primaries
  if (input.isPrimary) {
    const existingContacts = await getContacts(companyId);
    for (const contact of existingContacts) {
      if (contact.isPrimary && contact.id !== contactId) {
        await updateDoc(doc(contactsRef, contact.id), { isPrimary: false });
      }
    }
  }

  await updateDoc(docRef, {
    ...input,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a contact
 */
export async function deleteContact(
  companyId: string,
  contactId: string
): Promise<void> {
  const docRef = doc(getContactsRef(companyId), contactId);
  await deleteDoc(docRef);
}

// ===========================================
// Utility Functions
// ===========================================

/**
 * Check if a user can edit a company
 * (creator or admin)
 */
export function canEditCompany(
  company: Company,
  userId: string,
  isAdmin: boolean
): boolean {
  return isAdmin || company.createdBy === userId;
}

/**
 * Get company statistics
 */
export async function getCompanyStats(): Promise<{
  total: number;
  byType: Record<string, number>;
}> {
  const companies = await getCompanies();

  const byType: Record<string, number> = {};
  for (const company of companies) {
    const type = company.type || 'Uncategorized';
    byType[type] = (byType[type] || 0) + 1;
  }

  return {
    total: companies.length,
    byType,
  };
}

// ===========================================
// Partner Invite Functions (Cloud Function calls)
// ===========================================

interface SendPartnerInviteInput {
  companyId: string;
  companyName: string;
  contactEmail: string;
  contactName: string;
}

interface SendPartnerInviteResult {
  success: boolean;
  tokenId?: string;
  message?: string;
}

/**
 * Send partner portal invite email to a company
 * Creates a partner token and sends email via Brevo
 */
export async function sendPartnerInvite(input: SendPartnerInviteInput): Promise<SendPartnerInviteResult> {
  return log.operation('sendPartnerInvite', async () => {
    const sendInvite = httpsCallable<SendPartnerInviteInput, SendPartnerInviteResult>(
      functions,
      'crm_sendPartnerInvite'
    );

    const result = await sendInvite(input);
    log.info('sendPartnerInvite success', { companyId: input.companyId, email: input.contactEmail });
    return result.data;
  }, { companyId: input.companyId, email: input.contactEmail });
}
